import React, { ForwardedRef } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import styled from 'styled-components';

import { MenuItemButton, MenuItemLink } from './shared/navigation.styles';
import { INavigationItem } from './shared/types';

import ChevronIconAnimation from '../../atoms/icons/ChevronIconAnimation';

import { DEQ_TYPES } from '../../../utils/deq';
import { getInternalLink } from '../../../utils/generic';

import mq from '../../../styles/breakpoints';
import Spacings from '../../../styles/Spacings';
import { ThemeName } from '../../../styles/themes';

import { Widths } from '../../../tokens';

import { useNavigation } from './NavigationContextProvider';

interface Props {
  onItemClick: (activeItem?: INavigationItem) => void;
  activeTopItem?: INavigationItem;
  forwardedRef?: ForwardedRef<HTMLDivElement>;
}

const DesktopNavigation = ({
  onItemClick,
  activeTopItem,
  forwardedRef,
}: Props) => {
  const data = useStaticQuery(graphql`
    query desktopNavigationMenu {
      desktopItems: contentfulMenuItem(name: { eq: "Desktop navigation" }) {
        subitems {
          displayName
          description
          externalPage
          page {
            ... on ContentfulPageOverview {
              ...ParentPageOverview
            }
            ... on ContentfulPageGeneric {
              ...ParentPageGeneric
            }
            ... on ContentfulPageWizard {
              ...ParentPageWizard
            }
          }
          subitems {
            displayName
            description
            externalPage
            page {
              ... on ContentfulPageOverview {
                ...ParentPageOverview
              }
              ... on ContentfulPageGeneric {
                ...ParentPageGeneric
              }
              ... on ContentfulPageWizard {
                ...ParentPageWizard
              }
            }
            subitems {
              displayName
              description
              externalPage
              page {
                ... on ContentfulPageOverview {
                  ...ParentPageOverview
                }
                ... on ContentfulPageGeneric {
                  ...ParentPageGeneric
                }
                ... on ContentfulPageWizard {
                  ...ParentPageWizard
                }
              }
              subitems {
                displayName
                description
                externalPage
                page {
                  ... on ContentfulPageOverview {
                    ...ParentPageOverview
                  }
                  ... on ContentfulPageGeneric {
                    ...ParentPageGeneric
                  }
                  ... on ContentfulPageWizard {
                    ...ParentPageWizard
                  }
                }
                subitems {
                  displayName
                  description
                  externalPage
                  page {
                    ... on ContentfulPageOverview {
                      ...ParentPageOverview
                    }
                    ... on ContentfulPageGeneric {
                      ...ParentPageGeneric
                    }
                    ... on ContentfulPageWizard {
                      ...ParentPageWizard
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `);

  const { desktopItems } = data;

  const { setPosition } = useNavigation();

  const updatePosition = (index: number, newPosition: number) => {
    setPosition(index, newPosition);
  };

  return (
    <TopBar visible={desktopItems.subitems.length > 0} ref={forwardedRef}>
      {desktopItems.subitems.map((item: INavigationItem, key: number) => {
        return item.page ? (
          <MenuItemLink
            key={key}
            href={`/${getInternalLink(item.page)}`}
            onClick={() => updatePosition(0, key)}
            level={0}
            active={activeTopItem === item}
            data-testid="direct-page-item"
            data-element-type={DEQ_TYPES.btnMenu}
            data-component-type={DEQ_TYPES.mainNavigation}
            data-element-label={item.displayName.toLowerCase()}
            data-element-hierarchy={item.displayName.toLowerCase()}
            data-element-target={`/${getInternalLink(item.page)}`}
            data-element-position={key}
          >
            {item.displayName}
          </MenuItemLink>
        ) : (
          <MenuItemButton
            key={key}
            type="button"
            onClick={() => {
              updatePosition(0, key);
              onItemClick(activeTopItem === item ? undefined : item);
            }}
            level={0}
            active={activeTopItem === item}
            data-testid="category-item"
            data-element-type={DEQ_TYPES.btnMenu}
            data-component-type={DEQ_TYPES.mainNavigation}
            data-element-label={item.displayName.toLowerCase()}
            data-element-hierarchy={item.displayName.toLowerCase()}
            data-element-position={key}
          >
            {item.displayName}
            <ChevronWrapper active={activeTopItem === item}>
              <ChevronIconAnimation />
            </ChevronWrapper>
          </MenuItemButton>
        );
      })}
    </TopBar>
  );
};

export default DesktopNavigation;

const TopBar = styled.div<{ visible: boolean }>`
  align-items: center;
  box-sizing: border-box;
  color: white;
  display: flex;
  margin: 0 auto;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  position: relative;
  transition: opacity 0.3s ease-in;
  width: max-content;
  z-index: 5;

  ${mq.from.breakpoint.L`
    width: 100%;
    max-width: ${Widths.PAGE_MAX};
    padding: 0 0 0 ${({ theme }) =>
      theme.name === ThemeName.DEFAULT ? '60px' : Spacings.XS};
  `}
`;

const ChevronWrapper = styled.div<{ active: boolean }>`
  display: flex;
  ${({ active }) => (active ? 'transform: rotate(180deg);' : null)}
  transition: transform 0.15s ease;
`;
